import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, fileUrl } from "../lib/api";
import { Trash2, CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function CartDrawer({ open, onOpenChange }) {
  const { items, remove, total } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!user) { toast.message("Log in to check out"); onOpenChange(false); nav("/login?redirect=/cart"); return; }
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        item_ids: items.map((i) => i.id),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 bg-paper border-l-4 border-[#1A1A1A] flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="p-5 border-b-4 border-[#1A1A1A]">
          <SheetTitle className="font-display text-3xl uppercase text-left">Your loot</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <p className="font-display text-2xl uppercase">Empty as a Sunday closet.</p>
            <button onClick={() => { onOpenChange(false); nav("/shop"); }} className="brut-btn bg-orange text-white px-6 py-3 mt-6" data-testid="drawer-shop-btn">Go shop</button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
              {items.map((it) => (
                <div key={it.id} className="brut-sm p-2 flex items-center gap-3 bg-white" data-testid={`drawer-row-${it.id}`}>
                  <div className="w-16 h-20 brut-sm overflow-hidden flex-shrink-0">
                    {it.image ? <img src={fileUrl(it.image)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#f3efe6]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-sm uppercase truncate">{it.title}</div>
                    <div className="font-bold mt-1 text-sm">${Number(it.price).toFixed(2)}</div>
                  </div>
                  <button onClick={() => remove(it.id)} className="brut-btn bg-white px-2 py-2" data-testid={`drawer-remove-${it.id}`}>
                    <Trash2 size={14} strokeWidth={3} />
                  </button>
                </div>
              ))}
            </div>
            <div className="p-5 border-t-4 border-[#1A1A1A]">
              <div className="flex justify-between font-display text-2xl mb-3"><span>Total</span><span>${total.toFixed(2)}</span></div>
              <div className="flex gap-2">
                <button onClick={() => { onOpenChange(false); nav("/cart"); }} className="brut-btn bg-white px-4 py-3 text-sm flex-shrink-0" data-testid="drawer-view-cart">View cart</button>
                <button onClick={checkout} disabled={loading} className="brut-btn bg-orange text-white py-3 flex-1 flex items-center justify-center gap-2" data-testid="drawer-checkout-btn">
                  <CreditCard size={18} strokeWidth={3} />
                  {loading ? "…" : "Checkout"}
                </button>
              </div>
              <p className="text-[11px] mt-2 opacity-60">Use coupons on the cart page.</p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
